<script setup lang="ts">
import { computed } from 'vue';
import { createTextStyle } from '@/common/customisation/utils/styleUtils';
import type { RatingValue } from './RatingEmoji.types';

export interface RatingEmojiProps {
  value: RatingValue;
  label: string;
  /** Pre-resolved icon src for this option's current state (owned by the display mode config). */
  imageSrc: string;
  labelColor?: string;
  selectedLabelColor?: string;
  isSelected?: boolean;
  /** When true, the selected icon scales up. Driven by the active display mode. */
  animate?: boolean;
}

// Boolean props default to false in Vue, so no explicit defaults are needed.
const props = defineProps<RatingEmojiProps>();

const emit = defineEmits<{
  (event: 'click', value: RatingValue): void;
}>();

const handleClick = () => {
  emit('click', props.value);
};

const labelStyle = computed(() => {
  return createTextStyle(props.isSelected ? props.selectedLabelColor : props.labelColor);
});
</script>

<template>
  <div
    :class="['rating-emoji', 'card-radius', { 'rating-emoji--selected': isSelected, 'rating-emoji--animate': animate }]"
    :aria-label="label"
    :data-testid="`csat-rating-${value}`"
    tabindex="0"
    role="button"
    @click="handleClick"
  >
    <div class="rating-emoji__icon">
      <img :src="imageSrc" :alt="label" class="rating-emoji__image" />
    </div>
    <span class="rating-emoji__label theme-font-h5" :style="labelStyle">{{ label }}</span>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/mixin.scss';

.rating-emoji {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 8px;

  @include respond(phone) {
    gap: 4px;
    padding: 4px;
  }

  &--animate.rating-emoji--selected {
    .rating-emoji__icon {
      transform: scale(1.2);
    }
  }
}

.rating-emoji__icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.2s ease,
    filter 0.2s ease;
  user-select: none;

  @include respond(phone) {
    width: 40px;
    height: 40px;
  }
}

.rating-emoji__image {
  width: 100%;
  height: 100%;
}

.rating-emoji__label {
  text-align: center;
  user-select: none;
  transition:
    color 0.2s ease,
    font-weight 0.2s ease;
}

.rating-emoji--selected {
  .rating-emoji__label {
    font-weight: 500;
  }
}
</style>
